import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { readJson, stringField } from '@/lib/jsonBody';
import type { TelegramOidcInitResponse } from '@/services/generated/Api';

const SCRIPT_SRC = 'https://oauth.telegram.org/js/telegram-login.js?6';

export type TelegramLoginInit = TelegramOidcInitResponse;

function getLoginApi(): TelegramLoginApi | undefined {
  return window.Telegram?.Login;
}

let scriptPromise: Promise<void> | null = null;

export function loadTelegramLoginScript(): Promise<void> {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
      if (existing) {
        if (getLoginApi()) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('telegram_script_failed')));
        return;
      }

      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('telegram_script_failed'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export async function fetchTelegramLoginInit(): Promise<TelegramLoginInit> {
  const response = await fetch(`${getApiBaseUrl()}/api/auth/telegram/oidc/init`, {
    headers: { 'X-Requested-With': 'shot' },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('telegram_init_failed');
  const body = await readJson(response);
  const clientId = stringField(body, 'clientId');
  const nonce = stringField(body, 'nonce');
  if (!clientId || !nonce) throw new Error('telegram_init_failed');
  return { clientId, nonce };
}

// WHY: звать прямо из click-обработчика — попап открывается только пока жест пользователя на стеке; init здесь не
// await'ить
export function openTelegramLogin(init: TelegramLoginInit, lang?: string): Promise<string> {
  const login = getLoginApi();
  if (!login) return Promise.reject(new Error('telegram_script_failed'));

  return new Promise((resolve, reject) => {
    login.auth(
      { client_id: Number(init.clientId), scope: ['profile', 'write'], nonce: init.nonce, lang },
      (data) => {
        const idToken = stringField(data, 'id_token');
        if (idToken) resolve(idToken);
        else reject(new Error(stringField(data, 'error') ?? 'telegram_login_failed'));
      },
    );
  });
}
